"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components/PharmacyList/PharmacyList";

import usePlaces from "../../hooks/usePlaces";
import { useTour } from "../../../appointments/hooks/useTour";
import { infTourSteps } from "../../pages/Inf/infTourConfig";

import "./Nurses.css";

export default function Nurses({ LayoutComponent = AppLayoutDoctor }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20); // initial items

  const { items, loading, error, loadCategory, selectPlace } = usePlaces();

  // Start nurses tour if requested via URL
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "inf";
    if (hasGuide || hasTour) {
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        const infList = document.querySelector(".nurses-list-scroll");
        if (searchInput && infList) setShouldStartTour(true);
        else setTimeout(() => setShouldStartTour(true), 1500);
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, infTourSteps);

  // Load nurses category
  useEffect(() => {
    loadCategory("nurses", { limit: 50, skip: 0 });
  }, [loadCategory]);

  // Filter + slice for infinite scroll
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = (searchQuery || "").trim().toLowerCase();
    const filteredList = !q
      ? list
      : list.filter(
          (n) =>
            (n?.name || "").toLowerCase().includes(q) ||
            (n?.address || "").toLowerCase().includes(q) ||
            (n?.vicinity || "").toLowerCase().includes(q)
        );
    return filteredList.slice(0, visibleCount); // only show visibleCount
  }, [items, searchQuery, visibleCount]);

  // Infinite scroll: load more on scroll
  useEffect(() => {
    const container = document.querySelector(".nurses-list-scroll");
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
        setVisibleCount((prev) => prev + 20); // load 20 more
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

	  return (
    <LayoutComponent>
      <main className="nurses-main nurses--listOnly">
	        <div className="nurses-container">
	          <PharmacyHeader
	            title={t("locators.nurses.title")}
	            subtitle={t("locators.nurses.subtitle")}
            enableFilters={false}
            showTabs={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab="Nurse"
            setActiveTab={() => {}}
          />

          <section className="nurses-list-card">
	            <div className="nurses-list-scroll">
	              {loading && (
	                <p className="nurses-empty">{t("locators.nurses.loading")}</p>
	              )}
	              {!loading && error && (
	                <p className="nurses-empty">
	                  {t("locators.nurses.failed", { error: String(error) })}
	                </p>
	              )}
	              {!loading && !error && filtered.length === 0 ? (
	                <p className="nurses-empty">{t("locators.nurses.empty")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(n) => selectPlace(n)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>
        </div>
      </main>
    </LayoutComponent>
  );
}
